import { useEffect, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';

export type SelectOption = {
  value: string;
  label: string;
};

type AsyncSelectFieldProps = {
  label: string;
  description?: string;
  value?: string;
  required?: boolean;
  rawErrors?: string[];
  disabled?: boolean;

  loadingText?: string;
  emptyText?: string;
  disabledText?: string;

  dependency?: string;

  onChange: (value: string) => void;
  loadOptions: () => Promise<SelectOption[]>;
};

export function AsyncSelectField({
  label,
  description,
  value,
  required,
  rawErrors,
  disabled,
  loadingText = 'Loading...',
  emptyText = 'Select a value',
  disabledText = 'Selection unavailable',
  dependency,
  onChange,
  loadOptions,
}: AsyncSelectFieldProps) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    if (disabled) {
      setOptions([]);
      setError(undefined);
      setLoading(false);
    } else {
      const load = async () => {
        try {
          setLoading(true);
          setError(undefined);

          const result = await loadOptions();

          if (!cancelled) {
            setOptions(result);
          }
        } catch (e) {
          if (!cancelled) {
            setOptions([]);
            setError(e instanceof Error ? e.message : 'Failed to load options');
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      load();
    }
    return () => {
      cancelled = true;
    };
  }, [loadOptions, disabled, dependency]);

  let emptyOptionText = emptyText;

  if (disabled) {
    emptyOptionText = disabledText;
  } else if (loading) {
    emptyOptionText = loadingText;
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={Boolean(error) || Boolean(rawErrors?.length)}
    >
      <InputLabel shrink required={required}>
        {label}
      </InputLabel>

      <Select
        displayEmpty
        value={value ?? ''}
        onChange={event => onChange(event.target.value as string)}
        disabled={disabled || loading}
        renderValue={selected => {
          const selectedValue = selected as string;

          if (selectedValue) {
            return (
              options.find(option => option.value === selectedValue)?.label ??
              selectedValue
            );
          }

          if (disabled) {
            return disabledText;
          }

          if (loading) {
            return loadingText;
          }

          return emptyText;
        }}
      >
        <MenuItem value="">
          <em>{emptyOptionText}</em>
        </MenuItem>

        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{error ?? description}</FormHelperText>
    </FormControl>
  );
}
